/**
 * Pages tab: "Where views land" segments, the page-filter reading of that bar,
 * and the nearest-page suggestion for a page filter that matches nothing.
 * Views are additive, so every share here divides page views by the site's
 * total views. Unique visitors never go into these bars.
 */

export type LandingSegment = {
  key: string
  label: string
  views: number
  /** Share of the total views, 0 to 100 */
  share: number
  /** Tailwind background class */
  fill: string
  /** The filtered page when a page filter is active */
  highlighted?: boolean
}

type PageViews = { page: string; page_views: number }

export const LANDING_TOP_N = 4
export const LANDING_FILLS = ['bg-chart-1', 'bg-chart-2', 'bg-chart-3', 'bg-chart-4']
/** Paper mist-200 (#E5E5E5): the remainder, and every page outside a page filter */
export const LANDING_MUTED_FILL = 'bg-neutral-200 dark:bg-neutral-700'
/** The filtered page takes the second ramp step, as in Paper */
export const LANDING_FILTERED_FILL = 'bg-chart-2'

const REST_KEY = '__rest'

/** Top pages by views plus one "N other pages" remainder. */
export function buildLandingSegments(
  rows: PageViews[],
  totalViews: number,
  totalPages: number,
  topN = LANDING_TOP_N,
): LandingSegment[] {
  if (totalViews <= 0) return []
  const top = [...rows]
    .sort((a, b) => b.page_views - a.page_views)
    .slice(0, topN)
    .filter((p) => p.page_views > 0)
  const segments: Omit<LandingSegment, 'share'>[] = top.map((p, i) => ({
    key: p.page || '/',
    label: p.page || '/',
    views: p.page_views,
    fill: LANDING_FILLS[i] ?? LANDING_MUTED_FILL,
  }))
  const topViews = segments.reduce((sum, seg) => sum + seg.views, 0)
  const restViews = Math.max(0, totalViews - topViews)
  const restPages = Math.max(0, totalPages - segments.length)
  if (restViews > 0 && restPages > 0) {
    segments.push({
      key: REST_KEY,
      label: `${restPages.toLocaleString('en-US')} other ${restPages === 1 ? 'page' : 'pages'}`,
      views: restViews,
      fill: LANDING_MUTED_FILL,
    })
  }
  return segments.map((seg) => ({ ...seg, share: (seg.views / totalViews) * 100 }))
}

/**
 * The same bar while a page filter is active. Every unfiltered segment stays in
 * place and turns grey; the filtered page keeps its slot in color. A filtered
 * page outside the top pages gets its own slot just before the remainder, and
 * the remainder shrinks by the same views so the bar still sums to the site.
 */
export function buildFilteredLandingSegments(
  siteRows: PageViews[],
  siteTotalViews: number,
  siteTotalPages: number,
  filteredPage: string,
  filteredViews: number,
  topN = LANDING_TOP_N,
): LandingSegment[] {
  const base = buildLandingSegments(siteRows, siteTotalViews, siteTotalPages, topN)
  if (base.length === 0 || siteTotalViews <= 0) return []
  const match = base.find((seg) => seg.key !== REST_KEY && seg.key === filteredPage)
  if (match) {
    return base.map((seg) =>
      seg === match
        ? { ...seg, fill: LANDING_FILTERED_FILL, highlighted: true }
        : { ...seg, fill: LANDING_MUTED_FILL },
    )
  }
  if (filteredViews <= 0) return base.map((seg) => ({ ...seg, fill: LANDING_MUTED_FILL }))
  const out: LandingSegment[] = []
  for (const seg of base) {
    if (seg.key === REST_KEY) {
      out.push({
        key: filteredPage,
        label: filteredPage,
        views: filteredViews,
        share: (filteredViews / siteTotalViews) * 100,
        fill: LANDING_FILTERED_FILL,
        highlighted: true,
      })
      const restViews = Math.max(0, seg.views - filteredViews)
      if (restViews > 0) {
        out.push({ ...seg, views: restViews, share: (restViews / siteTotalViews) * 100, fill: LANDING_MUTED_FILL })
      }
    } else {
      out.push({ ...seg, fill: LANDING_MUTED_FILL })
    }
  }
  if (!out.some((seg) => seg.highlighted)) {
    out.push({
      key: filteredPage,
      label: filteredPage,
      views: filteredViews,
      share: (filteredViews / siteTotalViews) * 100,
      fill: LANDING_FILTERED_FILL,
      highlighted: true,
    })
  }
  return out
}

type FilterClauseLike = { field: string; operator: string; value: string }

/** The single "page is X" filter, or null when there is none or several. */
export function getActivePageFilter(filters: FilterClauseLike[] | undefined): string | null {
  const pageFilters = (filters ?? []).filter((f) => f.field === 'page' && f.operator === ':')
  return pageFilters.length === 1 && pageFilters[0].value ? pageFilters[0].value : null
}

/** Case-insensitive Levenshtein distance. */
export function editDistance(a: string, b: string): number {
  const s = a.toLowerCase()
  const t = b.toLowerCase()
  if (s === t) return 0
  if (!s.length) return t.length
  if (!t.length) return s.length
  let prev = Array.from({ length: t.length + 1 }, (_, i) => i)
  for (let i = 1; i <= s.length; i++) {
    const curr = [i]
    for (let j = 1; j <= t.length; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost)
    }
    prev = curr
  }
  return prev[t.length]
}

export type NearestPage = { page: string; views: number; distance: number }

/**
 * The page with views closest to a filter value that matched nothing: smallest
 * edit distance, then most views. A suggestion further than a third of the
 * value's length (at least 2 edits allowed) is not a near miss, so none.
 */
export function findNearestPage(query: string, pages: PageViews[]): NearestPage | null {
  const q = query.trim()
  if (!q) return null
  const limit = Math.max(2, Math.floor(q.length / 3))
  let best: NearestPage | null = null
  for (const p of pages) {
    if (!p.page || p.page_views <= 0) continue
    const distance = editDistance(q, p.page)
    if (distance === 0 || distance > limit) continue
    if (!best || distance < best.distance || (distance === best.distance && p.page_views > best.views)) {
      best = { page: p.page, views: p.page_views, distance }
    }
  }
  return best
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']

/** "one letter off", "two letters off" */
export function describeEditDistance(distance: number): string {
  const n = NUMBER_WORDS[distance] ?? String(distance)
  return `${n} ${distance === 1 ? 'letter' : 'letters'} off`
}
